import React, { Component, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Layout, Spin } from "antd";
import "./App.less";
import "./assets/scss/App.scss";

import MenuNavigation from "./services/MenuNavigation";
import UmkMainNav from "./components/menu/UmkMainNav";
import ScrollTop from "./ScrollTop";
import UmkGoTop from "./components/unimake-gotop/UmkGoTop";
import UmkFooter from "./components/unimake-footer/UmkFooter";
import AdicionarItemCarrinho from "./services/Loja/AdicionarItemCarrinho";
import CookieBanner from "./components/cookies/Cookies";

import ReactGA from "react-ga4";

const TRACKING_ID = "G-45QY2YELRT";
ReactGA.initialize(TRACKING_ID);

const { Header, Content } = Layout;

const HomePage = lazy(() => import("./pages/Home/HomePage"));
const DownloadsPage = lazy(() => import("./pages/Downloads/DownloadsPage"));
const ProdutosHome = lazy(() => import("./pages/Produtos/Home/ProdutosHome"));
const CadastroClientePage = lazy(() => import("./pages/ClienteArea/CadastroClientePage"));
const LoginClientePage = lazy(() => import("./pages/ClienteArea/LoginClientePage"));
const EsqueciMinhaSenhaPageCliente = lazy(() => import("./pages/ClienteArea/EsqueciMinhaSenhaClientePage"));
const AlterarSenhaClientePage = lazy(() => import("./pages/ClienteArea/AlterarSenhaClientePage"));
const AlterarSenhaClienteAuthPage = lazy(() => import("./pages/ClienteArea/AlterarSenhaClienteAuthPage"));
const DashboardPage = lazy(() => import("./pages/ClienteArea/DashboardClientePage"));
const ProfileClientePage = lazy(() => import("./pages/ClienteArea/ProfileClientePage"));
const LicencaPage = lazy(() => import("./pages/ClienteArea/LicencaPage"));
const TrabalheConoscoPage = lazy(() => import("./pages/TrabalheConosco/TrabalheConoscoPage"));
const CarrinhoPage = lazy(() => import("./pages/Loja/CarrinhoPage"));
const FecharCompraPage = lazy(() => import("./pages/Loja/FecharCompraPage"));
const LGPDPage = lazy(() => import("./pages/LGPD/LGPDPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const PageLoadingSkeleton = () => (
  <div className="page-loading-container">
    <div className="skeleton-content">
      <div className="skeleton-header">
        <div className="skeleton-line skeleton-title"></div>
        <div className="skeleton-line skeleton-subtitle"></div>
      </div>
      <div className="skeleton-body">
        <div className="skeleton-line"></div>
        <div className="skeleton-line"></div>
        <div className="skeleton-line skeleton-short"></div>
        <div className="skeleton-line"></div>
        <div className="skeleton-line skeleton-short"></div>
      </div>
    </div>
    
    <div className="spinner-overlay">
      <Spin size="large" tip="Carregando página..." />
    </div>
  </div>
);

interface AppState {
  isInitialLoad: boolean;
}

class App extends Component<{}, AppState> {
  private Menu: MenuNavigation;
  private preloadTimer: NodeJS.Timeout | null = null;

  constructor(props: any) {
    super(props);
    this.Menu = new MenuNavigation(props);
    this.state = {
      isInitialLoad: true
    };
  }

  componentDidMount() {
    if (!document.getElementById('skeleton-styles')) {
      const style = document.createElement('style');
      style.id = 'skeleton-styles';
      document.head.appendChild(style);
    }

    this.preloadTimer = setTimeout(() => {
      this.preloadCriticalPages();
      this.setState({ isInitialLoad: false });
    }, 2000);
  }

  componentWillUnmount() {
    if (this.preloadTimer) {
      clearTimeout(this.preloadTimer);
    }
  }

  preloadCriticalPages = () => {
    setTimeout(() => {
      Promise.all([
        import("./pages/Produtos/Home/ProdutosHome").catch(() => {}),
        import("./pages/ClienteArea/LoginClientePage").catch(() => {}),
        import("./pages/Downloads/DownloadsPage").catch(() => {})
      ]).then(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Páginas críticas pré-carregadas');
        }
      });
    }, 2000);
  };

  render() {
    return (
      <Layout>
        <Router>
          <ScrollTop>
            <Header className="main-header">
              <UmkMainNav />
            </Header>

            <Content className="main-content">
              <Suspense fallback={<PageLoadingSkeleton />}>
                <Routes>
                  <Route
                    path={`${process.env.PUBLIC_URL}/`}
                    element={this.Menu.OpenPage(<HomePage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/central-downloads`}
                    element={this.Menu.OpenPage(<DownloadsPage />)}
                  />

                  {/* Produtos */}
                  <Route
                    path={`${process.env.PUBLIC_URL}/produtos`}
                    element={this.Menu.OpenPage(<ProdutosHome />)}
                  />

                  {/* Cliente */}
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/cadastro`}
                    element={this.Menu.OpenPage(<CadastroClientePage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/login`}
                    element={this.Menu.OpenPage(<LoginClientePage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/esqueci-minha-senha`}
                    element={this.Menu.OpenPage(<EsqueciMinhaSenhaPageCliente />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/alterar-senha`}
                    element={this.Menu.OpenPage(<AlterarSenhaClientePage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/alterar-senha-auth`}
                    element={this.Menu.OpenPage(<AlterarSenhaClienteAuthPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/dashboard`}
                    element={this.Menu.OpenPage(<DashboardPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/perfil`}
                    element={this.Menu.OpenPage(<ProfileClientePage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/licenca/editar`}
                    element={this.Menu.OpenPage(<LicencaPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/licenca/renovar`}
                    element={this.Menu.OpenPage(<LicencaPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/licenca/desativar`}
                    element={this.Menu.OpenPage(<LicencaPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/cliente/licenca/reativar`}
                    element={this.Menu.OpenPage(<LicencaPage />)}
                  />

                  {/* Trabalhe Conosco */}
                  <Route
                    path={`${process.env.PUBLIC_URL}/trabalhe-conosco`}
                    element={this.Menu.OpenPage(<TrabalheConoscoPage />)}
                  />

                  {/* Loja */}
                  <Route
                    path={`${process.env.PUBLIC_URL}/loja/carrinho`}
                    element={this.Menu.OpenPage(<CarrinhoPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/loja/fechar`}
                    element={this.Menu.OpenPage(<FecharCompraPage />)}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/adicionar-ao-carrinho/:id/:quantidade`}
                    element={<AdicionarItemCarrinho />}
                  />

                  {/* LGPD */}
                  <Route
                    path={`${process.env.PUBLIC_URL}/lgpd`}
                    element={this.Menu.OpenPage(<LGPDPage />)}
                  />

                  {/* 404 */}
                  <Route 
                    path="*"
                    element={this.Menu.OpenPage(<NotFoundPage />)}
                  />
                </Routes>
              </Suspense>
            </Content>

            <CookieBanner />
            <UmkGoTop />
            <UmkFooter />
          </ScrollTop>
        </Router>
      </Layout>
    );
  }
}

export default App;